import { useSearchParams } from "react-router";
import { z } from "zod";
import { useCallback } from "react";

export const SourcingRequestSortSchema = z.enum(["name", "updatedAt", "createdAt", "neededBy"]);
export type SourcingRequestSort = z.infer<typeof SourcingRequestSortSchema>;

export const SourcingRequestSortOrderSchema = z.enum(["asc", "desc"]);
export type SourcingRequestSortOrder = z.infer<typeof SourcingRequestSortOrderSchema>;

const FilterSchema = z.object({
    search: z.string().optional(),
    sortBy: z
        .string()
        .transform(val => (SourcingRequestSortSchema.safeParse(val).success ? SourcingRequestSortSchema.parse(val) : undefined))
        .optional(),
    sortOrder: z
        .string()
        .transform(val => (SourcingRequestSortOrderSchema.safeParse(val).success ? SourcingRequestSortOrderSchema.parse(val) : undefined))
        .optional(),
    assignedToUserId: z
        .string()
        .transform(val => {
            const num = Number(val);
            return isNaN(num) ? undefined : num;
        })
        .optional(),
    limit: z
        .string()
        .transform(val => Number(val))
        .optional(),
    offset: z
        .string()
        .transform(val => Number(val))
        .optional(),
});

export type SourcingRequestFilterParams = z.infer<typeof FilterSchema>;

export function parseSourcingRequestFilterParams(searchParams: URLSearchParams): SourcingRequestFilterParams {
    const filterKeys = Object.keys(FilterSchema.shape);
    const searchParamKeyVal = filterKeys.map(key => [key, searchParams.get(key)]);
    const searchParamObjectEntries = searchParamKeyVal.filter(([_, value]) => value !== null);
    const searchParamObject = Object.fromEntries(searchParamObjectEntries);
    return FilterSchema.parse(searchParamObject);
}

export function useSourcingRequestsFilters() {
    const [searchParams, setSearchParams] = useSearchParams();
    const filters = parseSourcingRequestFilterParams(searchParams);

    const setFilters = useCallback(
        (newFilters: Partial<SourcingRequestFilterParams>) => {
            setSearchParams(prev => {
                const params = new URLSearchParams(prev);

                Object.entries(newFilters).forEach(([key, value]) => {
                    if (value === undefined) {
                        params.delete(key);
                    } else {
                        params.set(key, String(value));
                    }
                });

                return params;
            });
        },
        [setSearchParams],
    );

    const clearFilters = () => {
        setSearchParams(new URLSearchParams());
    };

    const getActiveFilterCount = () => {
        let count = 0;
        if (filters.assignedToUserId !== undefined) count += 1;
        return count;
    };

    return { filters, setFilters, clearFilters, getActiveFilterCount };
}
